<template>
  <div class="event-overview-container">
    <div class="edit-form-title">
      <h1>Overview</h1>
      <div class="package-header-note">
        Overview lets brands discover basic information regarding your event.
        As an event organizer, ensure that the information you provide aligns
        with the business objectives you are offering.
      </div>
    </div>
    <div class="edit-form">
      <label>Event Title
        <tooltip>
          A good title for the event is like a good headline.
          Write to the point and eye-catching. (Learn More)
        </tooltip>
      </label>
      <input type="text" v-model="title" placeholder="Ex: Los Angeles Music festival"
        class="event-name" name="displayName"
        maxlength="50"/>
      <span class="remaining-characters">{{50 - title.length}}/50</span>
    </div>
    <div class="edit-form">
      <label>One Line Pitch
        <tooltip>
          Grab the attention and pique the curiosity of potential
          sponsoring partners with a compelling business proposal.(Learn More)
        </tooltip>
      </label>
      <textarea type="text" placeholder="Ex:The perfect cult gathering of young...."
        v-model="subtitle" rows="4" name="onelinepitch"
        maxlength="160">
      </textarea>
      <span class="remaining-characters">{{160 - subtitle.length}}/160</span>
    </div>
    <div class="edit-form event-pic-container">
      <label>Event Image
        <tooltip>
        A good picture of the event is a story capturing the moments that captivate
        attendees heart. (Learn More)
        </tooltip>
      </label>
      <img v-if="pic" :src="pic" class="event-image"/>
      <div v-else class="no-image"></div>
      <image-uploader
        ref="imageUploader"
        imageType="event"
        :width="560"
        :height="320"
        shape="square"
        uploadUrl="/upload/event"
        :resourceId="eventId"
        @uploaded="url => pic = url">
        <button class="upload-image-button" aria-label="Photo Upload">
          <font-awesome-icon :icon="['fa', 'camera']" size="2x" />
          <span>Upload Event Picture</span>
        </button>
      </image-uploader>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Category
        <tooltip>
          Choose categories that are as specific as possible, but representative of your main event.
          Choosing the right event category will help your event stand out. (Learn More)
        </tooltip>
      </label>
      <v-select class="category-select" placeholder="Select your event category"
        v-model="category" :options="eventCategories" label="title">
      </v-select>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Tags
        <tooltip>
           Tags convey your own unique values and attitudes.
           Tags should express the essence of the purpose or mission of your event. (Learn More)
        </tooltip>
      </label>
        <v-select taggable multiple v-model="tags"
          class="tags-select" placeholder="Add your own custom tags">
        <template slot="no-options">
          Create your own tags...
        </template>
      </v-select>
      </div>
    <div class="edit-form edit-form-margin-top">
      <label>Venue
        <tooltip>
          Search your venue. It takes the right venue and location for an event
          to excite and motivate people to attend.(Learn More)
        </tooltip>
      </label>
      <div class="is-online">
        <input type="checkbox" v-model="isOnline" id="is-online"/>
        <label :for="`is-online`">Online Event</label>
      </div>
      <div class="location-select" v-if="!isOnline">
        <v-select label="title" :options="filteredLocations" :filterable="false"
          @search="debouncedGeoCode" name="location" v-model="location"
          placeholder="Type to search for Venue locations">
          <template slot="no-options">
            Search for locations...
          </template>
          <template slot="option" slot-scope="option">
            <span><b>{{ option.title }}</b></span>
            <span>{{ option.vicinity }}</span>
          </template>
        </v-select>
      </div>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>Start Date
        <tooltip>
          Your event can benefit from aligning itself with a date
          significant to your attendees.
        </tooltip>
      </label>
      <div class="event-date-start">
        <date-time-picker
          v-model="startDate"
          :options="{minDate: 'today'}"
          placeholder="Event Starts">
        </date-time-picker>
      </div>
    </div>
    <div class="edit-form edit-form-margin-top">
      <label>End Date
        <tooltip>
          When and what time the event gets over?
        </tooltip>
      </label>
      <div class="event-date-end">
        <date-time-picker
          v-model="endDate"
          :options="endDateOptions"
          placeholder="Event Ends">
        </date-time-picker>
      </div>
    </div>
    <div class="edit-form edit-form-margin-top" aria-label="Add Media Coverage"
      data-toggle="buttons">
      <label>Media Coverage
        <tooltip>
          When your events get the local media's attention, it puts a unique impression.
          No coverage is too small. (Learn More)
        </tooltip>
      </label>
      <div class="media-coverage-checkboxes">
        <div class="media-coverage-checkbox"
          v-for="(item, i) in mediaCoverages" :key="i">
          <input type="checkbox" :value="item.key" v-model="mediaCoverage"
            :id="`media-coverage-${item.key}`"/>
          <label :for="`media-coverage-${item.key}`">{{item.value}}</label>
        </div>
      </div>
    </div>
    <editable-links-container class="edit-form edit-form-margin-top"
      title="Event Links" :maxLinks="4" v-model="links" placeholder="https://eventbrite.com/...">
    </editable-links-container>
    <div class="edit-form">
      <button class="button" aria-label="Next" @click="goNext">Next</button>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapState } from 'vuex';
import ImageUploader from '@/components/ImageUploader.vue';
import DateTimePicker from '@/components/DateTimePicker.vue';
import geoCoder from '@/components/utils/geoCoder';
import EditableLinksContainer from '@/components/utils/EditableLinksContainer.vue';

export default {
  name: 'event-overview',
  components: {
    ImageUploader,
    DateTimePicker,
    EditableLinksContainer,
  },
  data() {
    return {
      eventId: 'empty',
      title: '',
      subtitle: '',
      category: '',
      tags: [],
      location: { title: '' },
      isOnline: false,
      pic: '',
      startDate: null,
      endDate: null,
      links: [],
      mediaCoverage: [],
      eventCategories: [],
      mediaCoverages: [],
      filteredLocations: [],
    };
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    endDateOptions() {
      return {
        minDate: this.startDate + (30 * 60 * 1000), // at least 30 minute event
      };
    },
    autoSaveProps() {
      return () => [
        this.title,
        this.subtitle,
        this.category,
        this.tags,
        this.location,
        this.isOnline,
        this.pic,
        this.startDate,
        this.endDate,
        this.links,
        this.mediaCoverage,
        this.eventCategories,
        this.mediaCoverages,
        this.filteredLocations,
      ].join('');
    },
  },
  created() {
    this.debouncedGeoCode = debounce(this.geoCode, 250);
    this.debouncedSave = debounce(this.save, 2000);
  },
  async mounted() {
    try {
      const eventCategoriesResponse =
        await this.backendAxios.get('/sponsorship-event/event-categories');
      this.eventCategories = eventCategoriesResponse.data;
      const mediaCoveragesResponse =
        await this.backendAxios.get('/sponsorship-event/media-coverages');
      this.mediaCoverages = mediaCoveragesResponse.data;
    } catch (err) {
      this.$toasted.show(err.response.data, {
        type: 'info',
        duration: 5000,
      });
    }
    if (this.$route.params.eventId === 'new') {
      // then this is a new event id that is being created
      this.eventId = null;
    } else {
      // fetch the event info using the id from backend
      this.eventId = this.$route.params.eventId;
      try {
        const fields = [
          'title',
          'subtitle',
          'category',
          'tags',
          'location',
          'isOnline',
          'pic',
          'mediaCoverage',
          'startDate',
          'endDate',
          'links',
        ];
        const eventResponse =
          await this.backendAxios
            .get(`/sponsorship-event/my-event/id/${this.eventId}?fields=${fields.join(',')}`);
        this.title = eventResponse.data.title;
        this.subtitle = eventResponse.data.subtitle;
        this.category = this.eventCategories
          .find(({ title }) => title === eventResponse.data.category);
        this.tags = eventResponse.data.tags;
        this.location = eventResponse.data.location;
        this.isOnline = eventResponse.data.isOnline;
        this.pic = eventResponse.data.pic;
        this.mediaCoverage = eventResponse.data.mediaCoverage;
        this.startDate = eventResponse.data.startDate;
        this.endDate = eventResponse.data.endDate;
        this.links = eventResponse.data.links;
        const unwatchAutoSaveProps = this.$watch(this.autoSaveProps, () => {
          this.debouncedSave();
        });
        this.$once('hook:destroyed', () => {
          unwatchAutoSaveProps();
        });
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
    }
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.initData();
  },
  methods: {
    initData() {
      this.title = '';
      this.subtitle = '';
      this.category = '';
      this.tags = [];
      this.isOnline = false;
      this.location = {};
      this.pic = '';
      this.mediaCoverage = [];
      this.startDate = null;
      this.endDate = null;
      this.links = [];
    },
    async save() {
      const {
        title, subtitle, category, tags, location, isOnline,
        mediaCoverage, startDate, endDate, links,
      } = this;
      const categoryTitle = category ? category.title : '';
      const locationData = location || {};
      try {
        const params = {
          title,
          subtitle,
          category: categoryTitle,
          tags,
          location: locationData,
          isOnline,
          mediaCoverage,
          startDate,
          endDate,
          links: links.filter(link => !!link),
        };
        await this.backendAxios
          .put(`/sponsorship-event/my-event/id/${this.eventId}`, params);
        return true;
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
      return false;
    },
    async goNext() {
      this.debouncedSave.cancel();
      if (await this.save()) {
        this.$emit('next', this.$route.name);
      }
    },
    async geoCode(searchString, loading) {
      loading(true);
      this.filteredLocations = await geoCoder(searchString);
      loading(false);
    },

    upload(url) {
      this.pic = url;
      this.$refs.imageUploader.upload(this.eventId);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.upload-container {
  position: relative;
  padding-bottom: 57.14%; /* 560:320 */
}

.upload-container > img {
  position: absolute;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.upload-inner {
  font-weight: 500;
  color: rgb(187, 187, 187);
  border: 2px dashed #DBDAE3;
  display: inline-block;
  transition: transform 0.3s;
}

.camera-icon {
  margin: 0.5rem 0;
}

.category-select, .tags-select {
  width: 100%;
  background-color: white;
  padding: 0 0 1rem 0;
}

.media-coverage-checkboxes {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 1rem 0;
}

.media-coverage-checkbox {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  min-width: 17rem;
}

.media-coverage-checkbox > input {
  width: 1rem;
}

.media-coverage-checkbox > label {
  margin: 0 2rem 0 0.25rem;
  padding: 0;
}

.is-online {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
}

.is-online > input {
  width: 1rem;
  margin-left: 0;
}

.location-select {
  width: 100%;
  background-color: white;
  padding: 0 0 1rem 0;
}

.picture-upload {
  top: 0rem;
  width: 100%;
  padding: 0 0 1rem 0;
}

.event-date-start, .event-date-end {
  width: 100%;
  background-color: white;
  padding: 0 0 1rem 0;
}

.upload-image-button {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 2px solid lightgray;
  color: #333333;
  background-color: white;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 5px 10px;
  border-radius: 0.325rem;
  letter-spacing: 1px;
  &:hover {
    filter: brightness(50%);
  }
}

.upload-image-button > span {
  margin-left: 0.5rem;
}

.event-image {
  width: 100%;
}

.event-pic-container {
  position: relative;
}

.event-pic-container .picture-upload {
  top: 3rem;
  left: 0.1rem;
  position: absolute;
}

.event-pic-container .no-image {
  height: 3rem;
}


</style>
